import React, { useState } from "react";
import logo from './asset/logo.png'
import { Shield, CheckCircle, XCircle, AlertTriangle, Mail, Scale, Clock, FileText, ChevronDown, ChevronUp, Menu, X } from "lucide-react";

export default function RefundsPage() {
  const [expandedSection, setExpandedSection] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      {/* Navbar */}
      <nav className="bg-black/80 backdrop-blur-md shadow-lg sticky top-0 z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <div className="flex items-center gap-2">
              <img
                  src={logo} // replace with your image path
                  alt="Description"
                  width="100px"
                />
              </div>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <a href="/" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
                Home
              </a>
            
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-gray-700"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-3 border-t border-gray-200">
              <a href="/" className="block text-gray-700 hover:text-blue-600 transition-colors font-medium">
                Home
              </a>
             
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <Shield className="w-5 h-5" />
            <span className="text-sm font-semibold">Legal Policy</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Cancellation & Refunds Policy
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Transparent guidelines for subscriptions, payments, and refunds on IEEEAssistant.in
          </p>
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-white/80">
            <Clock className="w-4 h-4" />
            <span>Last updated: {new Date().toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Introduction Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-100">
          <p className="text-gray-700 leading-relaxed">
            This <strong className="text-blue-600">Cancellation & Refunds Policy</strong> applies to purchases,
            subscriptions and services made on <strong className="text-purple-600">IEEEAssistant.in</strong> (the
            "Platform") and is governed by the laws of India. By subscribing to or
            using our services you agree to this Policy.
          </p>
        </div>

        {/* Quick Summary Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border-2 border-blue-200">
            <CheckCircle className="w-10 h-10 text-blue-600 mb-3" />
            <h3 className="font-bold text-gray-900 mb-2">Digital Service</h3>
            <p className="text-sm text-gray-700">Immediate access to AI tools and features</p>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border-2 border-purple-200">
            <XCircle className="w-10 h-10 text-purple-600 mb-3" />
            <h3 className="font-bold text-gray-900 mb-2">No-Refund Standard</h3>
            <p className="text-sm text-gray-700">Final after service access or use</p>
          </div>
          <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-xl p-6 border-2 border-pink-200">
            <AlertTriangle className="w-10 h-10 text-pink-600 mb-3" />
            <h3 className="font-bold text-gray-900 mb-2">Limited Exceptions</h3>
            <p className="text-sm text-gray-700">Refunds for technical errors only</p>
          </div>
        </div>

        {/* Detailed Sections */}
        <div className="space-y-4">
          {/* Section 1 */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300">
            <button
              onClick={() => toggleSection(1)}
              className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">1. Nature of Our Service</h2>
              </div>
              {expandedSection === 1 ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            {expandedSection === 1 && (
              <div className="px-6 pb-6 text-gray-700 leading-relaxed border-t border-gray-100 pt-6 animate-fadeIn">
                <p>
                  IEEEAssistant.in provides digital and SaaS services (AI writing, paper
                  generation, plagiarism checks, subscription features). These are
                  delivered electronically and access is granted immediately after
                  successful payment or during the subscription period. Because the
                  product is digital and can be consumed instantly, we follow a limited
                  refund policy as described below.
                </p>
              </div>
            )}
          </div>

          {/* Section 2 */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300">
            <button
              onClick={() => toggleSection(2)}
              className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">2. No-Refund Principle (Standard)</h2>
              </div>
              {expandedSection === 2 ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            {expandedSection === 2 && (
              <div className="px-6 pb-6 text-gray-700 leading-relaxed border-t border-gray-100 pt-6 animate-fadeIn">
                <p className="mb-4">
                  Except for the limited exceptions set out below, all purchases and
                  subscription fees are final and non-refundable once the service has
                  been made available or accessed, or when the user has used the
                  product (even partially). This includes:
                </p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">•</span>
                    <span>Change of mind, preference, or dissatisfaction after access.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">•</span>
                    <span>Unused monthly quotas or unused AI words at time of cancellation.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">•</span>
                    <span>Interruption caused by the user's internet or device issues.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">•</span>
                    <span>Perceived low utility of generated content (subjective).</span>
                  </li>
                </ul>
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    💡 <strong>Tip:</strong> We encourage customers to use free trials or freemium plans before subscribing.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Section 3 */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300">
            <button
              onClick={() => toggleSection(3)}
              className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">3. Limited Exceptions — When We May Refund</h2>
              </div>
              {expandedSection === 3 ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            {expandedSection === 3 && (
              <div className="px-6 pb-6 text-gray-700 leading-relaxed border-t border-gray-100 pt-6 animate-fadeIn">
                <p className="mb-4">
                  We will consider refunds only in the following verified situations:
                </p>
                <div className="space-y-3">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <strong className="text-green-800">Duplicate charge:</strong>
                    <p className="text-sm mt-1">If you were charged more than once for the same order due to a technical or payment gateway error.</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <strong className="text-green-800">Payment after cancellation:</strong>
                    <p className="text-sm mt-1">If you canceled a subscription prior to renewal but were still charged due to a technical error on our side.</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <strong className="text-green-800">Non-delivery / non-access:</strong>
                    <p className="text-sm mt-1">If you paid but did not receive access to the service and we cannot remedy the access issue within a reasonable time.</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <strong className="text-green-800">Legal requirement:</strong>
                    <p className="text-sm mt-1">Where a consumer forum or court orders a refund or applicable law requires a refund.</p>
                  </div>
                </div>
                <p className="mt-4 p-4 bg-gray-100 rounded-lg text-sm">
                  In all exception cases the refund (if approved) will be processed to
                  the original payment method. We aim to process approved refunds within
                  <strong className="text-blue-600"> 14 business days</strong> of approval.
                </p>
              </div>
            )}
          </div>

          {/* Section 4 */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300">
            <button
              onClick={() => toggleSection(4)}
              className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">4. Cancellation & Auto-Renewal</h2>
              </div>
              {expandedSection === 4 ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            {expandedSection === 4 && (
              <div className="px-6 pb-6 text-gray-700 leading-relaxed border-t border-gray-100 pt-6 animate-fadeIn">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <span>You may cancel your subscription anytime from your account settings.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <span>Cancellation stops future billing but does not entitle you to a refund for the current billing period.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <span>Subscriptions automatically renew at the end of the billing cycle unless canceled before renewal.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <span>It is your responsibility to cancel prior to renewal to avoid the next charge.</span>
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Section 6 */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300">
            <button
              onClick={() => toggleSection(6)}
              className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">6. How to Request a Refund</h2>
              </div>
              {expandedSection === 6 ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            {expandedSection === 6 && (
              <div className="px-6 pb-6 text-gray-700 leading-relaxed border-t border-gray-100 pt-6 animate-fadeIn">
                <ol className="space-y-4">
                  <li className="flex gap-4">
                    <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</span>
                    <div>
                      <p>Email <a href="mailto:support@ieeeassistant.in" className="text-blue-600 hover:underline font-semibold">support@ieeeassistant.in</a> within <strong>7 days</strong> of the transaction.</p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</span>
                    <div>
                      <p>Include: order ID / transaction ID, registered email, date/time of payment and a short description of the issue.</p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</span>
                    <div>
                      <p>Attach any supporting evidence (bank screenshots, gateway receipts, screenshots of errors).</p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">4</span>
                    <div>
                      <p>We will acknowledge your complaint within <strong>48 hours</strong> and investigate. If refund is approved, processing will take place in accordance with the payment provider timelines (we aim for 14 business days after approval).</p>
                    </div>
                  </li>
                </ol>
              </div>
            )}
          </div>

          {/* Section 8 */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300">
            <button
              onClick={() => toggleSection(8)}
              className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Scale className="w-5 h-5 text-indigo-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">8. Consumer Rights & Dispute Resolution</h2>
              </div>
              {expandedSection === 8 ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            {expandedSection === 8 && (
              <div className="px-6 pb-6 text-gray-700 leading-relaxed border-t border-gray-100 pt-6 animate-fadeIn">
                <p className="mb-4">
                  While this Policy aims to be fair and transparent, consumers in India
                  retain their statutory rights under the Consumer Protection Act, 2019.
                  If you remain dissatisfied after our internal grievance process you
                  may approach the appropriate consumer dispute redressal commission or
                  other competent forum.
                </p>
                <p>
                  For disputes we prefer to first try mediation and internal grievance
                  redressal. This Policy is governed by the laws of India and disputes
                  will be subject to the jurisdiction of the courts/consumer forums in
                  <strong> your company's registered city / state</strong>.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Contact Card */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white shadow-2xl">
          <div className="text-center">
            <Mail className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2">Need Help?</h3>
            <p className="mb-6 text-white/90">
              For refund requests or other issues, contact our support team
            </p>
            <a
              href="mailto:support@ieeeassistant.in"
              className="inline-block bg-white text-blue-600 px-8 py-3 rounded-full font-bold hover:bg-gray-100 transition-all duration-300 shadow-lg"
            >
              support@ieeeassistant.in
            </a>
          </div>
        </div>

        {/* Legal Notice */}
     
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-400">
            © 2025 IEEEAssistant.in. All rights reserved. | Governed by the laws of India
          </p>
        </div>
      </footer>
    </div>
  );
}